import { ConfigGroupPropUpdaterMap, ProviderConfigGroupUpdater, ProviderConfigUpdater } from '@1js/search-box-hostapp-interface';
import createWithRanker from '@1js/search-box-hostapp-interface/lib/search-box-ranking/createStreamPolicy';
import { Injectable, NgZone } from '@angular/core';
import { ExitSearchHandler, IO365ShellShim, SearchBoxSettings, SearchInstrumenter, TokenProvidersDictionary } from '@suiteux/suiteux-shell';
import React from 'react';
import { SearchConfigService } from '../search-results/services/search-config.service';
import { OfficeRoute } from '../shared/constants/office-route.enum';
import { KnownReferralPlatform } from '../shared/instrumentation/instrumentation.service';
import { FileItem, FileType } from '../shared/models/file-item.model';
import { Workload } from '../shared/models/workload.model';
import { MruDataService } from '../shared/mru/mru-data.service';
import { AccessTokenConsumer, AccessTokenService } from '../shared/services/access-token.service';
import { ConsumerSkuType } from '../shared/services/consumer-license.service';
import { CreateDocumentService } from '../shared/services/create-document.service';
import { DeviceDetector } from '../shared/services/device-detector.service';
import { DocumentService } from '../shared/services/document.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { OneDriveGraphService } from '../shared/services/onedrive-graph.service';
import { ShellShimService } from '../shared/services/shell-shim.service';
import { ThemingService } from '../shared/services/theming.service';
import { UrlLauncherService } from '../shared/services/url-launcher.service';
import { WindowService } from '../shared/services/window.service';
import { WorkloadsService } from '../shared/services/workloads.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from '../shared/shared-context/consumer-install-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';
import { UserContextService } from '../shared/shared-context/user-context.service';
import { KeyboardKey } from '../shared/utilities/keystroke-handler.utility';
import { FastSearchUXService } from './fast-searchux.service';
import { FilesFastSuggestionProvider } from './fast-suggestion-providers/files.fast-suggestion-provider';
import { CommercialApps } from './models/app.model';
import { Learnings as ConsumerLearnings } from './models/learning.model';
import { Settings as ConsumerAccountSettings } from './models/setting.model';
import { SearchInstrumentationService, SearchResultElementId, SearchResultType } from './search-instrumentation.service';
import { SearchInstrumentedXhrService } from './search-instrumented-xhr.service';
import { ISearchRoute, StartQueryOptions } from './search-page-bootstrapper/search-page-bootstrapper.service';
import { SearchPageInstrumentationService } from './search-page-instrumentation/search-page-instrumentation.service';
import { searchSuggestionStringMatchProviderRanker } from './search-ranker/search-suggestion-string-match-provider-ranker';
import { AppSuggestionProvider } from './search-suggestion-providers/app.search-suggestion-provider';
import { BaseSuggestionProvider } from './search-suggestion-providers/base.search-suggestion-provider';
import { BingSearchSuggestionProvider } from './search-suggestion-providers/bing.search-suggestion-provider';
import { FileSearchSuggestionProvider } from './search-suggestion-providers/file.search-suggestion-provider';
import { FileSearchSuggestionComponent } from './search-suggestion-providers/file.search-suggestion.renderer';
import { GoToSearchSuggestionProvider } from './search-suggestion-providers/goto.search-suggestion-provider';
import { HelpSearchSuggestionProvider } from './search-suggestion-providers/help.search-suggestion-provider';
import { HelpSearchSuggestionComponent } from './search-suggestion-providers/help.search-suggestion-renderer';
import { LearningSearchSuggestionProvider } from './search-suggestion-providers/learning.search-suggestion-provider';
import { getSearchGroupCreators } from './search-suggestion-providers/provider-configuration';
import { SeeMoreSuggestionProvider } from './search-suggestion-providers/see.more-suggestion-provider';
import { SerpSearchSuggestionProvider } from './search-suggestion-providers/serp.search-suggestion-provider';
import { SettingSearchSuggestionProvider } from './search-suggestion-providers/setting.search-suggestion-provider';
import { TellMeSearchSuggestionProvider } from './search-suggestion-providers/tellme.search-suggestion-provider';
import { TemplatePreviewSuggestionProvider } from './search-suggestion-providers/template.preview-suggestion-provider';
import { TemplatePreviewSuggestionComponent } from './search-suggestion-providers/template.preview-suggestion-renderer';
import { TemplateSuggestionProvider } from './search-suggestion-providers/template.search-suggestion-provider';
import { SharedSearchService } from './shared-search.service';

type ScopeType = 'person' | 'topic' | 'meeting';

// TODO: remove when type is added for submit search context in Midgard
// https://msfast.visualstudio.com/FAST/_workitems/edit/361628
interface ScopeData {
    readonly scopeType?: ScopeType;
    readonly data?: {
        readonly id?: string;
        readonly query?: string;
        readonly displayName?: string;
        readonly isPCSOverviewVerticalEnabled?: boolean;
    };
}

let exitSearchToken: ExitSearchHandler = undefined;

@Injectable({
    providedIn: 'root'
})
export class NativeSearchService {
    private static readonly migratedDriveEndpoint = 'https://my.microsoftpersonalcontent.com/personal/';
    private currentProps: ISearchRoute = { querySearchText: null, pathname: null };
    private shellShim: IO365ShellShim;
    private consumerSearchEnterEventAdded: boolean = false;
    private consumerOneDriveMigrated: boolean;

    private readonly keysDown: Set<string> = new Set<string>();

    constructor(
        private readonly accessTokenService: AccessTokenService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly commercialApps: CommercialApps,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly consumerLearnings: ConsumerLearnings,
        private readonly contextService: ContextService,
        private readonly createDocumentService: CreateDocumentService,
        private readonly deviceDetector: DeviceDetector,
        private readonly documentService: DocumentService,
        private readonly fastSearchUXService: FastSearchUXService,
        private readonly locService: LocService,
        private readonly mruDataService: MruDataService,
        private readonly ngZone: NgZone,
        private readonly officeRouteService: OfficeRouteService,
        private readonly oneDriveGraphService: OneDriveGraphService,
        private readonly searchConfigService: SearchConfigService,
        private readonly searchInstrumentationService: SearchInstrumentationService,
        private readonly searchInstrumentedXhrService: SearchInstrumentedXhrService,
        private readonly consumerAccountSettings: ConsumerAccountSettings,
        private readonly sharedSearchService: SharedSearchService,
        private readonly shellShimService: ShellShimService,
        private readonly themingService: ThemingService,
        private readonly urlLauncherService: UrlLauncherService,
        private readonly userContextService: UserContextService,
        private readonly windowService: WindowService,
        private readonly workloadsService: WorkloadsService,
        private readonly searchPageInstrumentationService: SearchPageInstrumentationService
    ) {}

    public async configureSearchBox() {
        this.shellShim = await this.shellShimService.getShellShim();
        const instrumenter = await this.searchPageInstrumentationService.searchUXInstrumenter;

        if (this.shellShim.Search) {
            this.setupNavigationHandler();

            if (this.officeRouteService.getCurrentRoute()?.app === FileType.Fluid) {
                this.shellShim.Search.SetSearchBoxVisibility(false);
            } else {
                this.shellShim.Search.SetSearchBoxVisibility(true);
            }

            if (this.clientStartupConfigService.config.authType === AuthType.MSA) {
                let settings: SearchBoxSettings;

                if (this.searchConfigService.config.consumerSearchOneDriveMigrationAwareEnabled) {
                    this.consumerOneDriveMigrated = await this.getOneDriveConvergenceStatusForConsumer();

                    const accessTokens = {
                        Substrate: () => this.accessTokenService.getAccessToken(AccessTokenConsumer.Substrate).then(response => response.token)
                    } as TokenProvidersDictionary;

                    settings = {
                        instrumenter,
                        useSharedCDN: true,
                        tokenProviders: accessTokens
                    } as SearchBoxSettings;
                } else {
                    settings = {
                        instrumenter,
                        useSharedCDN: true
                    } as SearchBoxSettings;
                }

                this.shellShim.Search.SetSearchBoxSettings(settings);
                this.shellShim.Search.SetProviderConfigUpdaters(this.getProviderConfigUpdatersForConsumer());
                this.shellShim.Search.OnSearchBoxActivated((searchText: string) => this.onConsumerSearchBoxActivated(searchText));
                this.shellShim.Search.OnSearchBoxDeactivated(() => this.onConsumerSearchBoxDeactivated());
                this.shellShim.Search.OnSearchTextChanged((searchText: string) => this.onConsumerSearchTextChanged(searchText));
                this.shellShim.Search.SetPlaceholderText(this.searchConfigService.config.resources.ConsumerSearchBoxStringExperimentA);
                this.registerSuggestionProviders();
                this.searchInstrumentationService.setSearchReferralPlatform(this.deviceDetector.isUwp());

                this.sharedSearchService.initializePrefetchLibrary();
                const main = await this.fastSearchUXService.searchUXMainModule;
                this.shellShim.Search.OnSubmitSearch(
                    main.createSubmitSearchHandler((searchText: string) => this.ngZone.run(() => this.onConsumerSearchBoxSubmitted(searchText)))
                );

                const { getScopeRouterApi } = main;

                getScopeRouterApi().then(({ createScopeRouter, listenToRoute }) => {
                    let init: boolean = true;
                    const scopeRouter = createScopeRouter(instrumenter, (route: ISearchRoute): undefined => {
                        if (init) {
                            this.currentProps = route;
                            init = false;
                        } else {
                            this.onRouteChanged(this.currentProps, route);
                            this.currentProps = route;
                        }

                        return undefined;
                    });

                    listenToRoute(scopeRouter, /* immidiate */ true);
                });
            } else {
                const accessTokens = {
                    BingForBusiness: () => this.accessTokenService.getAccessToken(AccessTokenConsumer.BingAnswers).then(response => response.token),
                    Graph: () => this.accessTokenService.getAccessToken(AccessTokenConsumer.Graph).then(response => response.token),
                    Loki: () => this.accessTokenService.getAccessToken(AccessTokenConsumer.Loki).then(response => response.token),
                    SharePoint: () => this.accessTokenService.getAccessToken(AccessTokenConsumer.SharePoint, false, this.contextService.sharedContext.sharePointResourceId).then(response => response.token),
                    Substrate: () => this.accessTokenService.getAccessToken(AccessTokenConsumer.Substrate).then(response => response.token)
                } as TokenProvidersDictionary; // TODO: TokenProvidersDictionary doesn't have an entry for Thor

                const settings = {
                    instrumenter,
                    useSharedCDN: true,
                    tokenProviders: accessTokens
                } as SearchBoxSettings;

                const startPageType = this.getStartPageTypeFromLocation(location.pathname, instrumenter);

                this.shellShim.Search.SetSearchBoxSettings(settings);
                this.shellShim.Search.SetProviderConfigUpdaters(this.getProviderConfigUpdaters());
                this.shellShim.Search.OnSearchBoxActivated(() => this.onSearchBoxActivated());

                if (
                    this.searchConfigService.config.templateProviderInCommercialEnabled ||
                    this.searchConfigService.config.helpProviderInCommercialEnabled
                ) {
                    this.registerSuggestionProviders();
                }

                this.sharedSearchService.initializePrefetchLibrary();
                this.fastSearchUXService.warmup3S().then(response => {
                    if (response.xhr.status === 404) {
                        this.shellShim.Search.SetSearchBoxVisibility(false);
                    }
                });

                const main = await this.fastSearchUXService.searchUXMainModule;
                const startQuery = this.fastSearchUXService.getStartQuery(main);
                this.shellShim.Search.OnSubmitSearch(
                    main.createSubmitSearchHandler((searchText: string, context?: ScopeData) =>
                        this.ngZone.run(() =>
                            this.searchConfigService.config.fastDisableStarQuery
                            ? startPageType === 'stream'
                                ? this.onSubmitStreamScopedSearch(searchText)
                                : context?.scopeType === 'person' && context?.data?.id
                                ? this.onSubmitPersonScopedSearch(context)
                                : context?.scopeType === 'topic' && context?.data?.id
                                ? this.onSubmitTopicScopedSearch(context)
                                : context?.scopeType === 'meeting' && context?.data?.id
                                    ? this.onSubmitMeetingScopedSearch(context)
                                : this.onSubmitSearch(searchText, startQuery)
                            : this.onSubmitSearchFallback(searchText, context)
                        )
                    )
                );
                this.removeAndSetExitSearch();
                const { getScopeRouterApi } = main;
                getScopeRouterApi().then(({ createScopeRouter, listenToRoute }) => {
                    const scopeRouter = createScopeRouter(instrumenter, (route: ISearchRoute): undefined => {
                        this.onRouteChanged(this.currentProps, route);
                        this.currentProps = route;
                        return undefined;
                    });
                    listenToRoute(scopeRouter);
                });
            }

            BaseSuggestionProvider.isSubscribedUser = await this.isSubscribedUser();
        }

        if (this.searchConfigService.config.typeToSearchEnabled) {
            this.typeToSearch();
        }
    }

    private getStartPageTypeFromLocation(pathname: string, instrumenter: SearchInstrumenter): string {
        /*
        Identifying the start page type from URL path
        */
        if (instrumenter.props.hostAppInfo.id.toLowerCase() === 'officehome' && pathname.toLowerCase().endsWith('stream')) {
            return 'stream';
        }

        return 'default';
    }

    private removeAndSetExitSearch() {
        exitSearchToken && this.shellShim?.Search?.RemoveOnExitSearch?.(exitSearchToken);
        exitSearchToken = this.shellShim?.Search?.OnExitSearch?.(() => this.ngZone.run(() => this.onExitSearch()));
    }

    private setupNavigationHandler() {
        const routeChangeStart = this.officeRouteService.getRouteChangeStartObservable();
        const routeChangeEnd = this.officeRouteService.getRouteChangeEndObservable();
        routeChangeStart.subscribe(async routes => {
            if (!!this.shellShim) {
                this.shellShim = await this.shellShimService.getShellShim();
            }
            if (routes.currentRoute.route === OfficeRoute.Content && routes.currentRoute.app && routes.currentRoute.app === FileType.Fluid) {
                this.shellShim.Search.SetSearchBoxVisibility(false);
            } else {
                this.shellShim.Search.SetSearchBoxVisibility(true);
            }
        });
        routeChangeEnd.subscribe(routes => {
            BaseSuggestionProvider.currentRoute = routes.currentRoute;
        });
    }

    private onSubmitStreamScopedSearch(searchText: string): void {
        if (!searchText) {
            return;
        }
        this.officeRouteService.navigateTo({ route: OfficeRoute.Search }, false, { q: searchText, refiner: '{"FileType": ["video"]}', oobRefiners: '{"FileType": ["video"]}' });
    }

    private onSubmitPersonScopedSearch(context?: ScopeData): void {
        if (context?.scopeType !== 'person' && !context?.data?.id) {
            return;
        }

        const route = context?.data?.isPCSOverviewVerticalEnabled ? OfficeRoute.SearchPersonScopedOverview : OfficeRoute.SearchPersonScoped;

        this.officeRouteService.navigateTo(
            { route },
            false,
            { pp: context.data.id, ...(context?.data?.query && { q: context?.data?.query }) }
        );
    }

    private onSubmitTopicScopedSearch(context?: ScopeData): void {
        if (context?.scopeType !== 'topic' && !context?.data?.id) {
            return;
        }
        this.officeRouteService.navigateTo({ route: OfficeRoute.SearchTopicScoped }, false, {
            topicId: context.data.id,
            topicName: context.data?.displayName
        });
    }

    private onSubmitMeetingScopedSearch(context?: ScopeData): void {
        if (context?.scopeType !== 'meeting' && !context?.data?.id) {
            return;
        }
        this.officeRouteService.navigateTo(
            { route: OfficeRoute.SearchMeetingScoped },
            /* replace: */ false,
            { meetingId: context.data.id , meetingName: context.data?.displayName }
        );
    }

    private async onSubmitSearch(searchText: string, startQuery?: (searchText: string, options?: StartQueryOptions) => Promise<void>): Promise<void> {
        if (!searchText) {
            return;
        }

        const instrumenter = await this.searchPageInstrumentationService.searchUXInstrumenter;

        // Start query early if enabled
        if (startQuery) {
            const options: StartQueryOptions = {
                destination: 'all',
                instrumenter
            };
            startQuery(searchText, options);
        }

        this.officeRouteService.navigateTo({ route: OfficeRoute.Search }, false, { q: searchText });
    }

    private onSubmitSearchFallback(searchText: string, context?: ScopeData): void {
        const params: { [key: string]: any } = { q: searchText };
        let route: OfficeRoute = OfficeRoute.Search;
        const isPersonScoped = context?.scopeType === 'person' && context?.data?.id;
        const isTopicScoped = context?.scopeType === 'topic' && context?.data?.id;
        const isMeetingScoped = context?.scopeType === 'meeting' && context?.data?.id;
        if (isPersonScoped) {
            params.pp = context.data.id;
            params.q = context.data.query || params.q;
            route = context?.data?.isPCSOverviewVerticalEnabled ? OfficeRoute.SearchPersonScopedOverview : OfficeRoute.SearchPersonScoped;
        } else if (isTopicScoped) {
            params.topicId = context?.data?.id;
            params.topicName = context?.data?.displayName;
            route = OfficeRoute.SearchTopicScoped;
        } else if (isMeetingScoped) {
            params.meetingId = context?.data?.id;
            params.meetingName = context?.data?.displayName;
            route = OfficeRoute.SearchMeetingScoped;
        }
        this.officeRouteService.navigateTo({ route }, false, params);
    }

    private onExitSearch(): void {
        const route = this.defaultBackRoute();
        this.officeRouteService.navigateTo({ route }, true);
    }

    private defaultBackRoute(): OfficeRoute | undefined {
        const currentRoute = this.officeRouteService.getCurrentRoute()?.route;
        switch (currentRoute) {
            case OfficeRoute.Search:
            case OfficeRoute.SearchTopicScoped:
            case OfficeRoute.SearchMeetingScoped:
            case OfficeRoute.SearchPersonScoped:
            case OfficeRoute.SearchPersonScopedOverview:
                return OfficeRoute.OfficeHome;
            case OfficeRoute.SearchSiteScoped:
                return OfficeRoute.Search;
            default:
                return undefined;
        }
    }

    private onRouteChanged(currentProps: ISearchRoute, props: ISearchRoute): void {
        if (props.querySearchText !== currentProps.querySearchText || props.pathname !== currentProps.pathname) {
            this.shellShim.Search.SetSearchText(props.querySearchText ? props.querySearchText : '');
        }
        this.removeAndSetExitSearch();
    }

    private async getOneDriveConvergenceStatusForConsumer(): Promise<boolean> {
        return this.oneDriveGraphService.getMyDrive().then(drive => {
            const webUrl = drive.webUrl;

            if (webUrl) {
                // A MSA user is considered to be migrated to the new SPO backend if the URL starts with a certain endpoint.
                // See: https://microsoft.sharepoint.com/teams/asgioast/_layouts/15/Doc.aspx?sourcedoc=%7B06EA1D16-8771-450B-B5C3-538D05488F04%7D&file=OneDrive%20Convergence.docx
                return webUrl.startsWith(NativeSearchService.migratedDriveEndpoint);
            }

            return false;
        }).catch(() => undefined);
    }

    private getProviderConfigUpdatersForConsumer(): Array<ProviderConfigUpdater> {
        const provider: any = [
            {
                id: 'search',
                groupCreators: getSearchGroupCreators(this.searchConfigService.config, this.deviceDetector.isMobileDevice()),
                groupUpdaters: [this.getFilesProviderUpdaterForConsumer()]
            }
        ];

        if (this.searchConfigService.config.enableConsumerSearchExactMatchRanker) {
            const streamPolicy = createWithRanker(searchSuggestionStringMatchProviderRanker);
            const rankerUpdater = {
                id: 'ranker',
                rankerUpdater: () => streamPolicy
            };

            provider.push(rankerUpdater);
        }

        return provider;
    }

    private getProviderConfigUpdaters(): Array<ProviderConfigUpdater> {
        const provider = [this.getPeopleProviderConfigUpdater(), this.getFilesProviderConfigUpdater(), this.getAppsProviderConfigUpdater()];

        const config = this.searchConfigService.config;

        if (
            !this.deviceDetector.isMobileDevice() &&
            config.templateProviderInCommercialEnabled &&
            config.templatePreviewProviderWithSeeMoreEnabled &&
            !this.userContextService.user.isGcc
        ) {
            provider.push(this.getTemplatePreviewProviderConfigUpdater());
        }

        if (config.helpProviderInCommercialEnabled) {
            provider.push(this.getHelpProviderConfigUpdater());
        }

        return provider;
    }

    private getHelpProviderConfigUpdater(): ProviderConfigUpdater {
        const _this = this;

        const provider = {
            id: 'helpProviderConfigUpdater',
            groupCreators: [
                {
                    groupId: 'help',
                    create() {
                        return {
                            position: 6,
                            optimalCount: 2,
                            maxCount: 3,
                            providers: [HelpSearchSuggestionProvider],
                            component: HelpSearchSuggestionComponent,
                            heading: _this.searchConfigService.config.resources.SearchSuggestionHelpHeading,
                            ariaLabel: _this.searchConfigService.config.resources.SearchSuggestionHelpHeading
                        };
                    }
                }
            ]
        };

        return provider;
    }

    private getTemplatePreviewProviderConfigUpdater(): ProviderConfigUpdater {
        const _this = this;

        const provider = {
            id: 'templatePreviewProviderConfigUpdater',
            groupCreators: [
                {
                    groupId: 'templatePreview',
                    create() {
                        return {
                            position: 7,
                            optimalCount: 4,
                            maxCount: 4,
                            providers: [TemplatePreviewSuggestionProvider],
                            component: TemplatePreviewSuggestionComponent,
                            heading: _this.searchConfigService.config.resources.SearchSuggestionTemplateHeading,
                            ariaLabel: _this.searchConfigService.config.resources.SearchSuggestionTemplateHeading
                        };
                    }
                }
            ]
        };

        return provider;
    }

    private getPeopleProviderConfigUpdater(): ProviderConfigUpdater {
        const peopleCardContainer = this.documentService.nativeDocument.createElement('div');
        this.documentService.nativeDocument.body.appendChild(peopleCardContainer);
        const _this = this;

        const provider = {
            id: 'personProviderConfigUpdater',
            groupUpdaters: [
                {
                    groupId: 'person',
                    createUpdater() {
                        return {
                            component: component => props =>
                                React.createElement(component, {
                                    ...props,
                                    onClick(e) {
                                        e.preventDefault();
                                        _this.sharedSearchService.setPersonAutosuggestClick(props, peopleCardContainer);
                                    }
                                })
                        };
                    }
                }
            ]
        };

        return provider;
    }

    private getFilesProviderConfigUpdater(): ProviderConfigUpdater {
        const _this = this;

        const provider = {
            id: 'fileProviderConfigUpdater',
            groupUpdaters: [
                {
                    groupId: 'file',
                    createUpdater() {
                        return {
                            providers(existingProviders) {
                                const providers = [...existingProviders];

                                if (_this.capabilitiesService.capabilities.isPwaInUwp) {
                                    providers.push(FilesFastSuggestionProvider);
                                }

                                return providers;
                            },
                            optimalCount(existingCount) {
                                return existingCount;
                            },
                            component: component => props =>
                                React.createElement(component, {
                                    ...props,
                                    onClick(e) {
                                        e.preventDefault();
                                        _this.sharedSearchService.setFilesAutosuggestClick(props);
                                    }
                                })
                        };
                    }
                }
            ]
        };

        return provider;
    }

    private getAppsProviderConfigUpdater(): ProviderConfigUpdater {
        let thirdPartyApps = new Array<Workload>();
        this.workloadsService.getThirdPartyWorkloads().subscribe(workloads => (thirdPartyApps = workloads));
        const _this = this;

        const provider = {
            id: 'appProviderConfigUpdater',
            groupUpdaters: [
                {
                    groupId: 'app',
                    createUpdater() {
                        return {
                            component: component => props =>
                                React.createElement(component, {
                                    ...props,
                                    onClick(e) {
                                        e.preventDefault();
                                        _this.sharedSearchService.setAppsAutosuggestClick(props, { ...e }, thirdPartyApps);
                                    }
                                })
                        };
                    }
                }
            ]
        };

        return provider;
    }

    private getFilesProviderUpdaterForConsumer(): ProviderConfigGroupUpdater {
        const _this = this;

        const updater: ProviderConfigGroupUpdater = {
            groupId: 'file',
            createUpdater(): Partial<ConfigGroupPropUpdaterMap & { position: () => number }> {
                return {
                    position() {
                        return 2;
                    },
                    optimalCount() {
                        return 2;
                    },
                    maxCount() {
                        return 5;
                    },
                    providers(existingProviders) {
                        const providers = [...existingProviders];

                        if (_this.consumerOneDriveMigrated) {
                            // The ODC user has been migrated to the new SPO backend, so do not use the BingConsumerDocumentSuggestionProvider,
                            // since Bing no longer contains up-to-date metadata of the user's OneDrive files, and we now need to rely on Substrate for file search.
                            const providerIndex = providers.findIndex(p => p.prototype?.getId?.() === 'searchux.suggestionProvider.provider.bingConsumerDocument');
                            if (providerIndex >= 0) {
                                providers.splice(providerIndex, 1);
                            }
                        } else {
                            // The ODC user has not been migrated to the new SPO backend, so do not use the AutoSuggestFileSuggestionProvider yet,
                            // since Bing is still the only place that contains the up-to-date metadata of the user's OneDrive files.
                            const providerIndex = providers.findIndex(p => p.prototype?.getId?.() === 'searchux.suggestionProvider.provider.autosuggestfile');
                            if (providerIndex >= 0) {
                                providers.splice(providerIndex, 1);
                            }
                        }

                        return providers;
                    },
                    component: fileComponent => props =>
                        React.createElement(
                            fileComponent as unknown as React.ComponentClass,
                            // tslint:disable-next-line:no-any
                            (Object as any).assign(
                                {},
                                { ...props },
                                {
                                    onClick(e: React.MouseEvent<HTMLAnchorElement>): void {
                                        e.preventDefault();
                                        const fileType = FileItem.fileTypeFromExtension(
                                            props.fileExtension && props.fileExtension[0] === '.'
                                                ? props.fileExtension.substr(1)
                                                : props.fileExtension
                                        );
                                        const referral = FileItem.referralFromFileType(fileType);
                                        _this.sharedSearchService.setFilesAutosuggestClick({
                                            ...props,
                                            customLoggingFunction: (success: boolean, knownReferralPlatform?: KnownReferralPlatform) => {
                                                if (success) {
                                                    _this.searchInstrumentationService.logSearchClickEvent(
                                                        SearchResultType.File,
                                                        SearchResultElementId.File,
                                                        true,
                                                        SearchResultType.File,
                                                        undefined,
                                                        referral,
                                                        undefined,
                                                        undefined,
                                                        knownReferralPlatform
                                                    );
                                                }
                                            },
                                            url: props.url,
                                            shortUrl: true
                                        });
                                    }
                                }
                            )
                        )
                };
            }
        };

        return updater;
    }

    private registerSuggestionProviders() {
        // Providers for both consumer and commercial
        BaseSuggestionProvider.capabilitiesService = this.capabilitiesService;
        BaseSuggestionProvider.clientStartupConfigService = this.clientStartupConfigService;
        BaseSuggestionProvider.contextService = this.contextService;
        BaseSuggestionProvider.createDocumentService = this.createDocumentService;
        BaseSuggestionProvider.deviceDetector = this.deviceDetector;
        BaseSuggestionProvider.locService = this.locService;
        BaseSuggestionProvider.ngZone = this.ngZone;
        BaseSuggestionProvider.officeRouteService = this.officeRouteService;
        BaseSuggestionProvider.searchConfigService = this.searchConfigService;
        BaseSuggestionProvider.searchInstrumentationService = this.searchInstrumentationService;
        BaseSuggestionProvider.sharedSearchService = this.sharedSearchService;
        BaseSuggestionProvider.shellShim = this.shellShim;
        BaseSuggestionProvider.urlLauncherService = this.urlLauncherService;
        HelpSearchSuggestionProvider.searchInstrumentedXhrService = this.searchInstrumentedXhrService;
        TellMeSearchSuggestionProvider.searchInstrumentedXhrService = this.searchInstrumentedXhrService;
        TemplateSuggestionProvider.buttonText = (matchedTemplate: string) =>
            this.locService.format(this.searchConfigService.config.resources.SearchSuggestionTemplateButtonText, matchedTemplate);
        TemplateSuggestionProvider.seeMoreButtonText = (searchText: string) =>
            this.locService.format(this.searchConfigService.config.resources.SearchSuggestionTemplateSeeMoreButtonText, searchText);

        if (this.capabilitiesService.capabilities.darkModeEnabled) {
            this.themingService.darkModeSubscription.subscribe(darkMode => {
                if (typeof darkMode !== 'undefined') {
                    BaseSuggestionProvider.isDarkMode = darkMode;
                }
            });
        }

        // Consumer only providers
        if (this.clientStartupConfigService.config.authType === AuthType.MSA) {
            Promise.all([this.workloadsService.getProvisionedWorkloads(), this.workloadsService.getAdditionalWorkloads()]).then(workloads => {
                const combinedWorkload: Array<Workload> = workloads[0].concat(workloads[1]);
                AppSuggestionProvider.allApps = combinedWorkload;
                GoToSearchSuggestionProvider.commercialApps = this.commercialApps;
                GoToSearchSuggestionProvider.commercialApps.initializeCommericalAppList(combinedWorkload);
            });

            this.consumerLearnings.initializeSettingList(this.shellShim);

            BingSearchSuggestionProvider.getButtonText = (searchText: string) =>
                this.locService.format(this.searchConfigService.config.resources.SearchSuggestionBingButtonText, searchText);
            FilesFastSuggestionProvider.capabilitiesService = this.capabilitiesService;
            FilesFastSuggestionProvider.clientStartupConfigService = this.clientStartupConfigService;
            FilesFastSuggestionProvider.contextService = this.contextService;
            FilesFastSuggestionProvider.deviceDetector = this.deviceDetector;
            FilesFastSuggestionProvider.mruDataService = this.mruDataService;
            FilesFastSuggestionProvider.secondaryDisplayTextEnabled = true;
            FilesFastSuggestionProvider.searchInstrumentationService = this.searchInstrumentationService;
            FilesFastSuggestionProvider.sharedSearchService = this.sharedSearchService;
            FileSearchSuggestionProvider.searchConfigService = this.searchConfigService;
            FileSearchSuggestionComponent.createFileTypeStringForNarrator = (fileType: string) =>
                this.locService.format(this.searchConfigService.config.resources.SearchSuggestionFileTypeNarratorText, fileType);
            LearningSearchSuggestionProvider.learnings = this.consumerLearnings;
            SeeMoreSuggestionProvider.getButtonText = (searchText: string) =>
                this.locService.format(this.searchConfigService.config.resources.SearchSuggestionSeeMoreButtonText, searchText);
            SettingSearchSuggestionProvider.settings = this.consumerAccountSettings;
            SerpSearchSuggestionProvider.getButtonText = () => this.searchConfigService.config.resources.SearchSuggestionSerpButtonText;
        }
    }

    private typeToSearch(): void {
        this.windowService.nativeWindow.addEventListener('keydown', async (event: KeyboardEvent) => {
            const activeElement = this.documentService.nativeDocument.activeElement;
            const tagName = activeElement ? activeElement.tagName.toLowerCase() : '';
            const contentEditableAttribute = activeElement ? activeElement.getAttribute('contenteditable') : null;

            if (tagName === 'input' || tagName === 'textarea' || (contentEditableAttribute && contentEditableAttribute.toLowerCase() === 'true')) {
                return;
            }

            const keyboardKey = event.keyCode;

            if (keyboardKey !== KeyboardKey.Shift) {
                this.keysDown.add(event.code);
            }

            if (
                this.keysDown.size === 1 &&
                (keyboardKey === KeyboardKey.Slash ||
                    (keyboardKey >= KeyboardKey.Zero && keyboardKey <= KeyboardKey.Nine) ||
                    (keyboardKey >= KeyboardKey.A && keyboardKey <= KeyboardKey.Z))
            ) {
                const shellShim = await this.shellShimService.getShellShim();

                shellShim.Search.FocusSearchBox(() => true);

                if (keyboardKey === KeyboardKey.Slash) {
                    event.preventDefault();
                }
            }
        });

        this.windowService.nativeWindow.addEventListener('keyup', (event: KeyboardEvent) => {
            const keyboardKey = event.keyCode;

            if (keyboardKey !== KeyboardKey.Shift) {
                this.keysDown.delete(event.code);
            }
        });
    }

    private onSearchBoxActivated(): void {
        this.fastSearchUXService.warmup3S();
        this.fastSearchUXService.loadMainModule();
        // prepareSearch is needed to lazy-load shared SERP code that is needed for early query.
        this.fastSearchUXService.prepareSearch();
    }

    private onConsumerSearchBoxActivated(searchText: string): void {
        // Temporarily disable enter key press.
        // An experiment to understand user intent when enter key is pressed in the search box.
        if (
            this.deviceDetector.isMobileDevice() ||
            (!this.consumerSearchEnterEventAdded && this.searchConfigService.config.disableSearchEnterPressedAction)
        ) {
            const searchBox = document.getElementsByClassName('ms-suiteux-search-box');
            if (searchBox && searchBox[0]) {
                searchBox[0].addEventListener('keypress', (e: KeyboardEvent) => {
                    const key = e.key || e.charCode || e.keyCode || 0;
                    if (document.activeElement.tagName.toLowerCase() === 'input' && (key === 13 || key === 'Enter')) {
                        this.searchInstrumentationService.setSearchSubmittedAction();
                        e.preventDefault();
                    }
                });
                this.consumerSearchEnterEventAdded = true;
            }
        }

        this.searchInstrumentationService.updateSearchSessionId();
        this.searchInstrumentationService.updateSearchText(searchText);
    }

    private onConsumerSearchBoxDeactivated(): void {
        this.searchInstrumentationService.logRenderedSearchResults();
        this.searchInstrumentationService.logSearchTextId();
    }

    private onConsumerSearchBoxSubmitted(searchText: string): void {
        this.searchInstrumentationService.setSearchSubmittedAction();

        if (searchText?.trim().length === 0) {
            return;
        }
        this.officeRouteService.navigateTo({ route: OfficeRoute.ConsumerAllSearch }, false, { q: searchText });
    }

    private onConsumerSearchTextChanged(searchText: string): void {
        this.searchInstrumentationService.updateSearchText(searchText);
    }

    private async isSubscribedUser(): Promise<boolean> {
        if (this.clientStartupConfigService.config.authType === AuthType.MSA) {
            const skuType = await this.consumerInstallConfigService.getCurrentSku();
            return (
                skuType !== null &&
                !(
                    skuType === ConsumerSkuType.NonActive ||
                    skuType === ConsumerSkuType.NotSubscribed ||
                    skuType === ConsumerSkuType.NotDetected ||
                    skuType === ConsumerSkuType.Perpetual ||
                    skuType === ConsumerSkuType.Perpetual2019
                )
            );
        }
        return true;
    }
}

export function updateScopeOnRouteChange(instrumenter: any, listenToRoute: any): () => void {
    return listenToRoute(({ scopes }: ISearchRoute): void => {
        const scope: any = scopes?.[0];
        const hostAppInfo: any = instrumenter.props.hostAppInfo;

        if (scope?.type === 'topic' && scope?.data) {
            return updateScopeDataOnInstrumenter(scope.type, {
                topicScope: scope.data
            });
        }

        if (scope?.type === 'person' && scope?.data) {
            return updateScopeDataOnInstrumenter(scope.type, {
                personScope: scope.data
            });
        }

        if (scope?.type === 'meeting' && scope?.data) {
            return updateScopeDataOnInstrumenter(scope.type, {
                meetingScope: scope.data
            });
        }

        if (scope?.type === 'message' && scope?.data) {
            return updateScopeDataOnInstrumenter(scope.type, {
                messageScope: scope.data
            });
        }

        // Don't do scoping if:
        // 2. Scope=folder-group. Scoping to a folder is deprecated. Should never happen
        // 3. Scope data hasn't been provided yet.
        if (scope && (scope.type === 'folder-group' || !scope.data)) {
            return;
        }
        if (!scope && hostAppInfo.scopeId) {
            updateScopeDataOnInstrumenter(undefined, {
                site: undefined,
                siteAssociation: undefined,
                personScope: undefined,
                topicScope: undefined,
                meetingScope: undefined,
                messageScope: undefined
            });
        }

        function updateScopeDataOnInstrumenter(scopeId: string | undefined, scopeData?: {}): void {
            instrumenter.setProps({
                hostAppInfo: {
                    ...hostAppInfo,
                    scopeId
                },
                scenarioContext: {
                    ...instrumenter.props.scenarioContext,
                    ...scopeData
                }
            });
        }
    });
}
